import { BookingCurrency, TutorLevel, TutorPackage } from '~/types/booking'
import { TutorOnDemandStat } from '~/panels/admin/pages/AdminPanel/AdminPanel.types'
import { formatThousands } from '~/utils'

import Base from './base'

function round(stat: string): number {
  return Math.round(Number(stat))
}

export default class AdminStat extends Base {
  public studentCount = Number(this.data.studentCount ?? 0)
  public newStudentCount = Number(this.data.newStudentCount ?? 0)

  public constructor(public data: TutorOnDemandStat) {
    super(data)
  }

  public videoChatEarnings(currency: BookingCurrency): string {
    const packageStat = this.data.packageStats[currency]?.[TutorPackage.MINUTES_15]
    return packageStat?.[TutorLevel.TUTOR]?.amount || '0'
  }

  public videoChatSessionsCount(currency: BookingCurrency): string {
    const packageStat = this.data.packageStats[currency]?.[TutorPackage.MINUTES_15]
    return packageStat?.[TutorLevel.TUTOR]?.earnings || '0'
  }

  public statForPackage(currency: BookingCurrency, tutorPackage: TutorPackage | 'byLevel'): string {
    const packageStat = this.data.packageStats[currency]?.[tutorPackage]
    const tutorPackages = packageStat?.[TutorLevel.TUTOR]?.amount || '0'
    const seniorTutorPackages = packageStat?.[TutorLevel.SENIOR]?.amount || '0'
    return `${tutorPackages}/${seniorTutorPackages}`
  }

  public hoursForPackage(currency: BookingCurrency, tutorPackage: TutorPackage | 'byLevel'): string {
    const packageStat = this.data.packageStats[currency]?.[tutorPackage]
    const tutorHours = packageStat?.[TutorLevel.TUTOR]?.hours || '0'
    const seniorTutorHours = packageStat?.[TutorLevel.SENIOR]?.hours || '0'

    return `${tutorHours}/${seniorTutorHours}`
  }

  public earningsForPackage(currency: BookingCurrency, tutorPackage: TutorPackage | 'byLevel'): string {
    const packageStat = this.data.packageStats[currency]?.[tutorPackage]
    const tutorEarnings = packageStat?.[TutorLevel.TUTOR]?.earnings || '0'
    const seniorTutorEarnings = packageStat?.[TutorLevel.SENIOR]?.earnings || '0'

    return `${formatThousands(round(tutorEarnings))}/${formatThousands(round(seniorTutorEarnings))}`
  }

  public averageSize(): { avgNok: number; avgUsd: number; avgTotal: number } {
    const avgNok = this.calculateAverage(BookingCurrency.NOK)
    const avgUsd = this.calculateAverage(BookingCurrency.USD)
    const avgTotal = this.calculateAverage()
    return {
      avgNok,
      avgUsd,
      avgTotal,
    }
  }

  public earnings(currency: BookingCurrency): string {
    return formatThousands(round(this.data.totalStats[currency]?.earnings || '0'))
  }

  public getEarnings(): {
    earningsNok: string
    earningsUsd: string
  } {
    const earningsNok = this.earnings(BookingCurrency.NOK)
    const earningsUsd = this.earnings(BookingCurrency.USD)
    return {
      earningsNok,
      earningsUsd,
    }
  }

  public sessionStat(): {
    completedHours: number
    pendingHours: number
    cancelledHours: number
    lateCancelledHours: number
    noShowcancelledHours: number
    totalHoursBought: number
    hoursUsed: number
  } {
    const { sessionStat } = this.data
    const totalHoursBought = this.getTotalHoursBought()
    return {
      completedHours: sessionStat.completedHours || 0,
      pendingHours: sessionStat.pendingHours || 0,
      cancelledHours: sessionStat.cancelledHours || 0,
      lateCancelledHours: sessionStat.lateCancelledHours || 0,
      noShowcancelledHours: sessionStat.noShowcancelledHours || 0,
      totalHoursBought,
      hoursUsed: sessionStat.hoursUsed || 0,
    }
  }

  private getTotalHoursBought(currency?: BookingCurrency): number {
    const currencies = currency ? [currency] : [BookingCurrency.NOK, BookingCurrency.USD]
    return currencies.reduce((acc, currentCurrency) => {
      return (
        acc +
        Object.values(TutorLevel).reduce((sum, tutorLevel) => {
          return sum + parseInt(this.data.packageStats?.[currentCurrency]?.['byLevel']?.[tutorLevel]?.hours ?? '0', 10)
        }, 0)
      )
    }, 0)
  }

  private getTotalPackagesBought(currency?: BookingCurrency): number {
    const currencies = currency ? [currency] : [BookingCurrency.NOK, BookingCurrency.USD]
    return currencies.reduce((acc, currentCurrency) => {
      return (
        acc +
        Object.values(TutorLevel).reduce((sum, tutorLevel) => {
          return sum + parseInt(this.data.packageStats?.[currentCurrency]?.['byLevel']?.[tutorLevel]?.amount ?? '0', 10)
        }, 0)
      )
    }, 0)
  }

  private calculateAverage(currency?: BookingCurrency): number {
    const hours = this.getTotalHoursBought(currency)
    const packages = this.getTotalPackagesBought(currency)
    if (!packages) return 0
    return Math.round((hours * 10) / packages) / 10
  }
}
